import React from "react";
import { useState } from "react";
import { Form, DrawerProps, Drawer } from "antd";
import {
  dataToFormDataConverter,
  validateFields,
} from "../../../../../config/Global";
import { assignErrorToInput } from "../../../../../store/api";
import SVGIcon from "../../../../../utils/SVGIcon";
import Config from "../../../../../config/Config";
import { createRecord } from "../../utils/slice";
import FormComponent from "./FormComponent";
import { useAppDispatch } from "../../../../../utils/hooks/hooks";
import CancelButton from "../../../../../components/Buttons/CancelButton";
import SaveButton from "../../../../../components/Buttons/SaveButton";

interface AddDrawerProps extends DrawerProps {
  titleName: string;
  open: boolean;
  close: () => void;
}

/**
 * Renders a drawer component for adding a new record in Acronyms module.
 *
 * @param {AddDrawerProps} props - The props for the component.
 * @param {string} props.titleName - The title name for the drawer.
 * @param {boolean} props.open - Whether the drawer is open or not.
 * @param {() => void} props.close - The function to close the drawer.
 * @return {ReactElement} The rendered drawer component.
 */
const AddComponent: React.FC<AddDrawerProps> = ({
  titleName,
  open,
  close,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const drawerClose = () => {
    close();
    form.resetFields();
    setDisabled(true);
  };

  /**
 * Handles the change of form values and updates the form fields accordingly.
 *
 * @param {any} data - The data to be validated and updated.
 * @return {void} This function does not return anything.
 */
  const onValuesChange = (data: any) => {
    validateFields(form, setDisabled);
    
  };

  /**
   * Handles the form submission by setting the saving state to true,
   * removing any empty or default values from the data object,
   * and dispatching an createRecord action with the converted data.
   *
   * @param {any} data - The form data to be submitted.
   * @return {void} This function does not return anything.
   */
  const handleFormSubmit = (data: any) => {
    setSaving(true);
    
   /** remove empty or default values from data object specifically 
     * for text Editor component
     */
    Object.keys(data).forEach((key)=>{
      if(data[key]==='<p style="font-size: 14px;"><br data-mce-bogus="1"></p>' ||
          data[key] === '<p style="font-size: 14px;" data-mce-style="font-size: 14px;"><br></p>'
      ){
        delete data[key]
      }
    })

    dispatch(createRecord(dataToFormDataConverter(data)))
      .then(() => {
        drawerClose();
      })
      .catch((error: any) => {
        assignErrorToInput(form, error?.STATUS);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Drawer
      title={`Add ${titleName}`}
      open={open}
      width={"70%"}
      placement="right"
      onClose={drawerClose}
      destroyOnClose
      closeIcon={<SVGIcon icon="close" color={Config.themePrimaryBtn} />}
      footer={[
        <CancelButton key={2} onClick={drawerClose} />,
        <SaveButton
          key={1}
          form="addForm"
          disabled={disabled}
          loading={saving}
        />,
      ]}
      {...rest}
    >
      <FormComponent
        form={form}
        id="addForm"
        handleSubmit={handleFormSubmit}
        onValuesChange={onValuesChange}
      />
    </Drawer>
  );
};

export default AddComponent;
